/**
 * 묘터 전용 검색 페이지 (/burial)
 * 기존 listings 컬렉션에서 property_type: "묘터" 필터링
 * 묘터 요청 모달 포함
 */
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useIsMobile } from "../../hooks/useIsMobile";
import { usePageTitle } from "../../hooks/usePageTitle";
import { AppHeader } from "../../components/AppHeader";
import { AppFooter } from "../../components/AppFooter";
import { KakaoMapView } from "../../components/KakaoMapView";
import { formatPrice } from "../../utils/format";
import type { ListingResponse } from "../listings/types";

const API_BASE = import.meta.env.VITE_API_URL || "";

const BURIAL_TYPES = ["전통묘지", "수목장", "납골당", "자연장", "봉안묘"];
const RELIGION_OPTIONS = ["무교", "기독교", "불교", "천주교"];
const SIDO_OPTIONS = [
  "전체", "서울", "경기", "인천", "강원", "충북", "충남", "대전", "세종",
  "경북", "경남", "대구", "울산", "부산", "전북", "전남", "광주", "제주",
];

// 묘터 특성 강조 문구
const BURIAL_PHRASES = [
  "조용하고 접근 편한 위치",
  "자연환경 우수, 관리 용이",
  "진입로 확보, 주차 가능",
  "풍수지리 양호",
  "인접 도로 양호",
];

export function BurialSearchPage() {
  usePageTitle("\uBB18\uD130 \uCC3E\uAE30");
  const m = useIsMobile();

  const [listings, setListings] = useState<ListingResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<"list" | "map">("list");

  // 필터
  const [sido, setSido] = useState("전체");
  const [maxPrice, setMaxPrice] = useState(50000); // 만원
  const [areaRange, setAreaRange] = useState<"all" | "small" | "mid" | "large">("all"); // 면적 (㎡)

  // 요청 모달
  const [showModal, setShowModal] = useState(false);
  const [reqForm, setReqForm] = useState({
    name: "", phone: "", region: "", burial_type: "", religion: "",
    budget_min: "", budget_max: "", memo: "",
  });
  const [reqLoading, setReqLoading] = useState(false);
  const [reqDone, setReqDone] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/listings?property_type=${encodeURIComponent("묘터")}&limit=200`)
      .then((r) => r.json())
      .then((d: any) => setListings(d.listings || []))
      .catch(() => setListings([]))
      .finally(() => setLoading(false));
  }, []);

  // 필터링
  const filtered = listings.filter((l) => {
    if (sido !== "전체" && l.region_sido !== sido) return false;
    if (l.price_manwon > maxPrice) return false;
    const areaSqm = l.area_pyeong * 3.305;
    if (areaRange === "small" && areaSqm >= 10) return false;
    if (areaRange === "mid" && (areaSqm < 10 || areaSqm >= 30)) return false;
    if (areaRange === "large" && areaSqm < 30) return false;
    return true;
  });

  // 요청 제출
  const handleRequest = async () => {
    if (!reqForm.name || !reqForm.phone || !reqForm.region || !reqForm.burial_type) {
      alert("이름, 연락처, 지역, 묘터 유형은 필수입니다");
      return;
    }
    setReqLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/burial/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: reqForm.name,
          phone: reqForm.phone.replace(/\D/g, ""),
          region: reqForm.region,
          burial_type: reqForm.burial_type,
          religion: reqForm.religion || undefined,
          budget_min: reqForm.budget_min ? Number(reqForm.budget_min) : undefined,
          budget_max: reqForm.budget_max ? Number(reqForm.budget_max) : undefined,
          memo: reqForm.memo || undefined,
        }),
      });
      if (!res.ok) throw new Error("요청 실패");
      setReqDone(true);
    } catch {
      alert("요청 전송에 실패했습니다. 다시 시도해 주세요.");
    } finally {
      setReqLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" as const, background: "var(--bg-primary)" }}>
      <AppHeader backTo="/listings" backLabel="\u2190 \uB9E4\uBB3C \uBAA9\uB85D" />

      {/* ── 히어로 섹션 ── */}
      <div style={{ background: "linear-gradient(135deg, #0F172A 0%, #1A2540 100%)", borderBottom: "1px solid rgba(212,168,83,0.2)", padding: m ? "32px 16px 28px" : "48px 32px 40px", textAlign: "center" as const }}>
        <p style={{ margin: "0 0 10px", fontSize: m ? 32 : 40 }}>{"⚰️"}</p>
        <h1 style={{ margin: "0 0 10px", fontSize: m ? 24 : 32, fontWeight: 800, color: "var(--text-primary)" }}>
          <span style={{ color: "var(--lt-gold)" }}>묘터</span> 전용 검색
        </h1>
        <p style={{ margin: 0, fontSize: m ? 13 : 15, color: "var(--text-muted)", maxWidth: 520, marginLeft: "auto", marginRight: "auto", lineHeight: 1.7 }}>
          전통묘지, 수목장, 납골당, 봉안묘 등 다양한 형태의 묘지 및 장지 매물을<br />
          NDA 체결 후 정확한 위치와 함께 확인하실 수 있습니다
        </p>
      </div>

      <div style={{ flex: 1, maxWidth: 1200, margin: "0 auto", width: "100%", padding: m ? "20px 16px 48px" : "32px 32px 60px" }}>

        {/* ── 필터바 ── */}
        <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-light)", borderRadius: "var(--radius-xl)", padding: m ? "14px" : "16px 20px", marginBottom: 20, display: "flex", gap: m ? 10 : 16, flexWrap: "wrap" as const, alignItems: "center" }}>

          {/* 지역 */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>지역</label>
            <select
              value={sido}
              onChange={(e) => setSido(e.target.value)}
              style={filterInput}
            >
              {SIDO_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* 면적 */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>면적</label>
            <select
              value={areaRange}
              onChange={(e) => setAreaRange(e.target.value as any)}
              style={filterInput}
            >
              <option value="all">전체</option>
              <option value="small">10㎡ 이하</option>
              <option value="mid">10~30㎡</option>
              <option value="large">30㎡ 이상</option>
            </select>
          </div>

          {/* 가격 슬라이더 */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: m ? "1 1 100%" : "1" }}>
            <label style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600, whiteSpace: "nowrap" as const }}>최대 가격</label>
            <input
              type="range"
              min={1000} max={100000} step={1000}
              value={maxPrice}
              onChange={(e) => setMaxPrice(Number(e.target.value))}
              style={{ flex: 1, accentColor: "#D4A853" }}
            />
            <span style={{ fontSize: 13, fontWeight: 700, color: "#FBBF24", minWidth: 80, textAlign: "right" as const }}>
              {maxPrice === 100000 ? "제한 없음" : `${maxPrice.toLocaleString()}만원`}
            </span>
          </div>

          {/* 뷰 전환 */}
          <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
            {(["list", "map"] as const).map((v) => (
              <button
                key={v}
                onClick={() => setViewMode(v)}
                style={{
                  padding: "6px 12px", borderRadius: "var(--radius-sm)", border: "1px solid",
                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                  borderColor: viewMode === v ? "#D4A853" : "var(--border-medium)",
                  background: viewMode === v ? "rgba(212,168,83,0.15)" : "transparent",
                  color: viewMode === v ? "#D4A853" : "var(--text-muted)",
                }}
              >
                {v === "list" ? "목록" : "지도"}
              </button>
            ))}
          </div>
        </div>

        {/* ── 결과 수 ── */}
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
            총 <strong style={{ color: "var(--lt-gold)" }}>{filtered.length}</strong>건의 묘터 매물
          </p>
        </div>

        {loading ? (
          <p style={{ color: "var(--text-muted)", textAlign: "center" as const, padding: "60px 0" }}>불러오는 중...</p>
        ) : filtered.length === 0 ? (
          /* ── 매물 없음 → 요청 CTA ── */
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-light)", borderRadius: "var(--radius-xl)", padding: m ? "32px 20px" : "48px 40px", textAlign: "center" as const }}>
            <p style={{ fontSize: 48, margin: "0 0 16px" }}>{"📭"}</p>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: "var(--text-primary)", margin: "0 0 8px" }}>
              등록된 묘터 매물이 없습니다
            </h3>
            <p style={{ fontSize: 14, color: "var(--text-muted)", margin: "0 0 24px", lineHeight: 1.7 }}>
              찾으시는 조건의 묘터 매물이 없습니다.<br />
              요청을 남기시면 전문 중개사가 직접 찾아드립니다.
            </p>
            <button
              onClick={() => setShowModal(true)}
              style={{ padding: "13px 28px", background: "linear-gradient(135deg,#B8972E,#D4AF37)", color: "#0F172A", border: "none", borderRadius: "var(--radius-md)", fontSize: 15, fontWeight: 700, cursor: "pointer" }}
            >
              {"⚰️"} 묘터 매물 요청하기
            </button>
          </div>
        ) : viewMode === "map" ? (
          /* ── 지도 뷰 ── */
          <KakaoMapView
            active={true}
            updateKey={`burial-${sido}-${maxPrice}`}
            height={m ? "400px" : "560px"}
            listings={filtered}
            allListings={filtered}
          />
        ) : (
          /* ── 목록 뷰 ── */
          <>
            <div style={{ display: "grid", gridTemplateColumns: m ? "1fr" : "repeat(auto-fill, minmax(340px, 1fr))", gap: 16 }}>
              {filtered.map((l, idx) => (
                <Link key={l.id} to={`/listings/${l.id}`} style={{ textDecoration: "none", display: "block" }}>
                  <div style={{
                    background: "var(--bg-card)",
                    border: "1px solid var(--border-light)",
                    borderLeft: "3px solid #7C3AED",
                    borderRadius: "var(--radius-xl)",
                    padding: "18px 20px",
                    boxShadow: "var(--shadow-sm)",
                    cursor: "pointer",
                    transition: "var(--transition-fast)",
                  }}
                    onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(1.07)")}
                    onMouseLeave={(e) => (e.currentTarget.style.filter = "")}
                  >
                    {/* 헤더 */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10, flexWrap: "wrap" as const, gap: 6 }}>
                      <div>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 12, background: "rgba(124,58,237,0.15)", color: "#A78BFA", marginRight: 6 }}>
                          {"⚰️"} 묘터
                        </span>
                        <span style={{ fontSize: 13, color: "var(--text-secondary)", fontWeight: 600 }}>
                          {l.region_sido} {l.region_sigungu}
                        </span>
                      </div>
                      <p style={{ margin: 0, fontSize: 17, fontWeight: 800, color: "#FBBF24" }}>
                        {formatPrice(l.price_manwon)}
                      </p>
                    </div>

                    {/* 정보 */}
                    <div style={{ display: "flex", gap: 16, marginBottom: 10, flexWrap: "wrap" as const }}>
                      <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        면적: <strong style={{ color: "var(--text-secondary)" }}>{l.area_pyeong?.toLocaleString()}평 ({Math.round(l.area_pyeong * 3.305)}㎡)</strong>
                      </span>
                      {l.land_category && (
                        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                          지목: <strong style={{ color: "var(--text-secondary)" }}>{l.land_category}</strong>
                        </span>
                      )}
                    </div>

                    {/* 묘터 특성 강조 문구 */}
                    <p style={{ margin: "0 0 10px", fontSize: 12, color: "#A78BFA", fontStyle: "italic" }}>
                      {"✦"} {BURIAL_PHRASES[idx % BURIAL_PHRASES.length]}
                    </p>

                    {/* 중개사 */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        {"👤"} {l.broker_name}
                        {l.broker_is_verified && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 10, background: "rgba(212,168,83,0.12)", color: "#D4A853" }}>✓ 인증</span>}
                      </span>
                      <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                        NDA 후 위치 공개
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* 하단 요청 CTA */}
            <div style={{ marginTop: 32, background: "rgba(124,58,237,0.06)", border: "1px solid rgba(124,58,237,0.2)", borderRadius: "var(--radius-xl)", padding: m ? "20px" : "24px 32px", display: "flex", flexDirection: m ? "column" as const : "row", alignItems: m ? "flex-start" : "center", justifyContent: "space-between", gap: 16 }}>
              <div>
                <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>원하는 조건의 묘터를 못 찾으셨나요?</p>
                <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>요청을 남기시면 전문 중개사가 직접 찾아드립니다</p>
              </div>
              <button
                onClick={() => setShowModal(true)}
                style={{ padding: "11px 24px", background: "linear-gradient(135deg,#B8972E,#D4AF37)", color: "#0F172A", border: "none", borderRadius: "var(--radius-md)", fontSize: 14, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" as const }}
              >
                {"⚰️"} 묘터 매물 요청하기
              </button>
            </div>
          </>
        )}
      </div>

      {/* ── 묘터 요청 모달 ── */}
      {showModal && (
        <div
          style={{ position: "fixed" as const, inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div
            ref={modalRef}
            style={{ background: "var(--bg-card)", border: "1px solid var(--border-light)", borderTop: "3px solid #7C3AED", borderRadius: "var(--radius-xl)", padding: m ? "24px 20px" : "32px", maxWidth: 520, width: "100%", maxHeight: "90vh", overflowY: "auto" as const }}
          >
            {reqDone ? (
              <div style={{ textAlign: "center" as const, padding: "20px 0" }}>
                <p style={{ fontSize: 48, margin: "0 0 16px" }}>{"✅"}</p>
                <h3 style={{ color: "var(--lt-gold)", margin: "0 0 8px" }}>요청이 접수되었습니다</h3>
                <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "0 0 24px", lineHeight: 1.7 }}>
                  전문 중개사가 확인 후 연락드리겠습니다.
                </p>
                <button onClick={() => { setShowModal(false); setReqDone(false); setReqForm({ name: "", phone: "", region: "", burial_type: "", religion: "", budget_min: "", budget_max: "", memo: "" }); }} style={{ padding: "11px 28px", background: "linear-gradient(135deg,#B8972E,#D4AF37)", color: "#0F172A", border: "none", borderRadius: "var(--radius-md)", fontWeight: 700, cursor: "pointer" }}>
                  닫기
                </button>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "var(--text-primary)" }}>{"⚰️"} 묘터 매물 요청</h3>
                  <button onClick={() => setShowModal(false)} style={{ background: "none", border: "none", fontSize: 20, color: "var(--text-muted)", cursor: "pointer" }}>{"×"}</button>
                </div>

                <div style={{ display: "flex", flexDirection: "column" as const, gap: 14 }}>
                  {[
                    { key: "name", label: "이름 *", placeholder: "홍길동", type: "text" },
                    { key: "phone", label: "연락처 *", placeholder: "010-0000-0000", type: "tel" },
                  ].map(({ key, label, placeholder, type }) => (
                    <div key={key}>
                      <label style={modalLabel}>{label}</label>
                      <input type={type} value={(reqForm as any)[key]} onChange={(e) => setReqForm((p) => ({ ...p, [key]: e.target.value }))} placeholder={placeholder} style={modalInput} />
                    </div>
                  ))}

                  <div>
                    <label style={modalLabel}>희망 지역 *</label>
                    <select value={reqForm.region} onChange={(e) => setReqForm((p) => ({ ...p, region: e.target.value }))} style={modalInput}>
                      <option value="">선택</option>
                      {SIDO_OPTIONS.filter((s) => s !== "전체").map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  <div>
                    <label style={modalLabel}>묘터 유형 *</label>
                    <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8 }}>
                      {BURIAL_TYPES.map((t) => (
                        <button key={t} type="button" onClick={() => setReqForm((p) => ({ ...p, burial_type: t }))} style={{ padding: "7px 14px", borderRadius: 20, border: "1px solid", fontSize: 13, fontWeight: 600, cursor: "pointer", borderColor: reqForm.burial_type === t ? "#7C3AED" : "var(--border-medium)", background: reqForm.burial_type === t ? "rgba(124,58,237,0.15)" : "transparent", color: reqForm.burial_type === t ? "#A78BFA" : "var(--text-muted)" }}>
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label style={modalLabel}>종교 (선택)</label>
                    <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8 }}>
                      {RELIGION_OPTIONS.map((r) => (
                        <button key={r} type="button" onClick={() => setReqForm((p) => ({ ...p, religion: p.religion === r ? "" : r }))} style={{ padding: "7px 14px", borderRadius: 20, border: "1px solid", fontSize: 13, fontWeight: 600, cursor: "pointer", borderColor: reqForm.religion === r ? "#D4A853" : "var(--border-medium)", background: reqForm.religion === r ? "rgba(212,168,83,0.12)" : "transparent", color: reqForm.religion === r ? "#D4A853" : "var(--text-muted)" }}>
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      <label style={modalLabel}>예산 최소 (만원)</label>
                      <input type="number" value={reqForm.budget_min} onChange={(e) => setReqForm((p) => ({ ...p, budget_min: e.target.value }))} placeholder="예: 1000" style={modalInput} />
                    </div>
                    <div>
                      <label style={modalLabel}>예산 최대 (만원)</label>
                      <input type="number" value={reqForm.budget_max} onChange={(e) => setReqForm((p) => ({ ...p, budget_max: e.target.value }))} placeholder="예: 5000" style={modalInput} />
                    </div>
                  </div>

                  <div>
                    <label style={modalLabel}>추가 요청사항 (선택)</label>
                    <textarea value={reqForm.memo} onChange={(e) => setReqForm((p) => ({ ...p, memo: e.target.value }))} rows={3} placeholder="기타 요청사항을 입력해 주세요" style={{ ...modalInput, resize: "vertical" as const }} />
                  </div>

                  <button onClick={handleRequest} disabled={reqLoading} style={{ padding: "13px", background: "linear-gradient(135deg,#B8972E,#D4AF37)", color: "#0F172A", border: "none", borderRadius: "var(--radius-md)", fontSize: 15, fontWeight: 700, cursor: "pointer", marginTop: 4, opacity: reqLoading ? 0.6 : 1 }}>
                    {reqLoading ? "전송 중..." : "요청 보내기"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <AppFooter />
    </div>
  );
}

const filterInput: React.CSSProperties = {
  padding: "6px 10px",
  borderRadius: "var(--radius-md)",
  border: "1px solid var(--border-medium)",
  background: "var(--bg-secondary)",
  color: "var(--text-primary)",
  fontSize: 13,
};

const modalLabel: React.CSSProperties = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "var(--text-secondary)",
  marginBottom: 6,
};

const modalInput: React.CSSProperties = {
  padding: "10px 14px",
  borderRadius: "var(--radius-md)",
  border: "1px solid var(--border-medium)",
  background: "var(--bg-secondary)",
  color: "var(--text-primary)",
  fontSize: 16,
  width: "100%",
  boxSizing: "border-box",
  outline: "none",
};
